
import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'
import {
  Play, Copy, CheckCheck, Database, Terminal,
  ChevronDown, Loader2, RotateCcw,
  TableProperties, Clock, AlertCircle, CheckCircle2, Flame,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'

// ─── Starter queries ──────────────────────────────────────────────────────────
const STARTER_QUERIES = [
  { label: 'List tables', sql: "SELECT name, type FROM sqlite_master WHERE type='table' ORDER BY name" },
  { label: 'Database info', sql: "SELECT * FROM sqlite_master" },
  { label: 'Create table', sql: "CREATE TABLE IF NOT EXISTS test_table (\n  id TEXT PRIMARY KEY DEFAULT (lower(hex(randomblob(16)))),\n  value TEXT NOT NULL,\n  created_at TEXT DEFAULT (datetime('now'))\n)" },
  { label: 'Insert row', sql: "INSERT INTO test_table (value) VALUES ('hello from LavaFlow')" },
  { label: 'Select all', sql: "SELECT * FROM test_table LIMIT 50" },
  { label: 'Count rows', sql: "SELECT COUNT(*) as total FROM test_table" },
]

// ─── Types ────────────────────────────────────────────────────────────────────
type QueryResult = {
  results: Record<string, unknown>[]
  success: boolean
  meta?: { duration: number; rows_read: number; rows_written: number; changes: number }
  error?: string
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={async () => { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
      className="p-1.5 rounded hover:bg-muted/80 transition-colors text-muted-foreground hover:text-foreground"
    >
      {copied ? <CheckCheck className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

// ─── Result table ─────────────────────────────────────────────────────────────
function ResultTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) return (
    <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
      <CheckCircle2 className="w-4 h-4 text-success" />
      Query executed — 0 rows returned
    </div>
  )
  const cols = Object.keys(rows[0])
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border">
            {cols.map(col => (
              <th key={col} className="px-3 py-2 text-left font-semibold text-muted-foreground whitespace-nowrap">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={cn('border-b border-border/50 hover:bg-muted/30 transition-colors', i % 2 === 0 ? '' : 'bg-muted/10')}>
              {cols.map(col => {
                const val = row[col]
                const str = val === null ? 'NULL' : String(val)
                return (
                  <td key={col} className={cn(
                    'px-3 py-2 font-mono whitespace-nowrap max-w-xs truncate',
                    val === null ? 'text-muted-foreground italic' : 'text-foreground'
                  )}>
                    {str}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PlaygroundPage() {
  const [isMac, setIsMac] = useState(false)
  useEffect(() => { setIsMac(/mac/i.test(navigator.userAgent)) }, [])

  const [sql, setSql] = useState(STARTER_QUERIES[0].sql)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<QueryResult | null>(null)
  const [history, setHistory] = useState<{ sql: string; ts: number; rows: number; ms: number }[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  async function runQuery() {
    if (!sql.trim() || running) return
    setRunning(true)
    setResult(null)
    const start = Date.now()
    try {
      const res = await fetch('/api/database/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: sql.trim(), params: [] }),
      })
      const data: QueryResult = await res.json()
      const elapsed = Date.now() - start
      setResult(data)
      if (data.success !== false) {
        setHistory(prev => [
          { sql: sql.trim(), ts: Date.now(), rows: data.results?.length ?? 0, ms: elapsed },
          ...prev.slice(0, 19),
        ])
      }
    } catch (err) {
      setResult({
        results: [],
        success: false,
        error: err instanceof Error ? err.message : 'Network error',
      })
    } finally {
      setRunning(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    // Ctrl/Cmd + Enter to run
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      runQuery()
    }
  }

  const rows = result?.results ?? []
  const cols = rows.length ? Object.keys(rows[0]) : []

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Top toolbar */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 border-b border-border bg-card/60 backdrop-blur-sm">
        <div className="flex items-center gap-2 mr-auto">
          <div className="w-6 h-6 rounded lava-gradient flex items-center justify-center">
            <Terminal className="w-3 h-3 text-lava-foreground" />
          </div>
          <span className="text-sm font-semibold text-foreground">SQL Console</span>
          <Badge variant="secondary" className="text-[10px] font-medium">EdgeSQL — Cloudflare D1</Badge>
        </div>

        {/* History toggle */}
        <button
          onClick={() => setShowHistory(v => !v)}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs transition-colors',
            showHistory ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
          )}
        >
          <Clock className="w-3.5 h-3.5" /> History
          <ChevronDown className={cn('w-3 h-3 transition-transform', showHistory && 'rotate-180')} />
        </button>

        <button
          onClick={() => { setSql(''); setResult(null) }}
          className="flex items-center gap-1 px-2 py-1.5 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
        </button>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: Editor + results */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0">
          {/* SQL Editor */}
          <div className="flex-shrink-0 relative border-b border-border">
            <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">SQL</span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground hidden sm:block">
                  {isMac ? '⌘' : 'Ctrl'}+Enter to run
                </span>
                <Button
                  onClick={runQuery}
                  disabled={!sql.trim() || running}
                  size="sm"
                  className="h-7 px-3 lava-gradient text-lava-foreground border-0 hover:opacity-90 text-xs gap-1.5 font-medium"
                >
                  {running
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Running</>
                    : <><Play className="w-3 h-3" /> Run</>
                  }
                </Button>
              </div>
            </div>
            <Textarea
              ref={textareaRef}
              value={sql}
              onChange={e => setSql(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[160px] max-h-[260px] resize-y font-mono text-[13px] bg-background border-0 rounded-none focus-visible:ring-0 p-4 text-foreground leading-relaxed"
              spellCheck={false}
              placeholder="-- Write your SQL here..."
            />
          </div>

          {/* Results pane */}
          <div className="flex-1 overflow-auto">
            {!result && !running && (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-12">
                <Database className="w-8 h-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Run a query to see results</p>
              </div>
            )}
            {running && (
              <div className="flex items-center justify-center h-32 gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin text-lava" />
                <span className="text-sm">Executing...</span>
              </div>
            )}
            {result && !running && (
              <div>
                {/* Result meta bar */}
                <div className="flex items-center gap-4 px-3 py-2 border-b border-border bg-muted/30 sticky top-0">
                  {result.success === false || result.error ? (
                    <div className="flex items-center gap-2 text-destructive text-xs">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="font-mono">{result.error}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 text-success text-xs">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span className="font-medium">Success</span>
                      </div>
                      <div className="flex items-center gap-4 text-[11px] text-muted-foreground ml-2">
                        {rows.length > 0 && (
                          <span className="flex items-center gap-1">
                            <TableProperties className="w-3 h-3" />
                            {rows.length} {rows.length === 1 ? 'row' : 'rows'} · {cols.length} cols
                          </span>
                        )}
                        {result.meta?.duration != null && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {result.meta.duration.toFixed(1)}ms
                          </span>
                        )}
                        {result.meta?.rows_written != null && result.meta.rows_written > 0 && (
                          <span>{result.meta.rows_written} row{result.meta.rows_written !== 1 ? 's' : ''} written</span>
                        )}
                      </div>
                      <div className="ml-auto flex items-center gap-1">
                        <CopyButton text={JSON.stringify(rows, null, 2)} />
                      </div>
                    </>
                  )}
                </div>
                {!result.error && <ResultTable rows={rows} />}
              </div>
            )}
          </div>
        </div>

        {/* Right: Sidebar — starter queries + history */}
        <div className="w-64 flex-shrink-0 border-l border-border bg-card/40 overflow-y-auto flex flex-col">
          {/* Starter queries */}
          <div className="p-3 border-b border-border">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Quick queries
            </p>
            <div className="space-y-1">
              {STARTER_QUERIES.map(q => (
                <button
                  key={q.label}
                  onClick={() => setSql(q.sql)}
                  className={cn(
                    'w-full text-left px-2.5 py-2 rounded-lg text-xs transition-colors',
                    sql.trim() === q.sql.trim()
                      ? 'bg-lava/10 text-lava border border-lava/20'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                  )}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          {/* History */}
          <div className="p-3 flex-1">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              History
            </p>
            {history.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">No queries run yet</p>
            ) : (
              <div className="space-y-1">
                {history.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => setSql(h.sql)}
                    className="w-full text-left px-2.5 py-2 rounded-lg text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                  >
                    <p className="font-mono truncate">{h.sql.split('\n')[0]}</p>
                    <p className="text-[10px] mt-0.5 flex items-center gap-2">
                      <span>{h.rows} rows</span>
                      <span>{h.ms}ms</span>
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Bottom promo */}
          <div className="p-3 border-t border-border">
            <div className="p-3 rounded-xl bg-lava/8 border border-lava/20 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 text-lava" />
                <p className="text-[11px] font-semibold text-foreground">LavaFlow EdgeSQL</p>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Sub-10ms reads from 100+ Cloudflare edge locations worldwide.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
